import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import TopBar from "@/components/TopBar";
import LoginPage from "@/pages/Login";
import LibraryPage from "@/pages/Library";
import TemplateDetailPage from "@/pages/TemplateDetail";
import TemplateEditorPage from "@/pages/TemplateEditor";
import SettingsPage from "@/pages/Settings";
import ChangePasswordPage from "@/pages/ChangePassword";
import ActivityPage from "@/pages/Activity";
import { ErrorBoundary } from "@/components/ErrorBoundary";

function Protected({ children, adminOnly = false, allowWhenMustChange = false }) {
    const { user, ready } = useAuth();
    const loc = useLocation();
    if (!ready) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ color: "var(--tms-muted)" }}>
                Loading…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
    // Force the password change flow before any other authenticated route.
    if (user.must_change_password && !allowWhenMustChange) {
        return <Navigate to="/change-password" replace state={{ from: loc }} />;
    }
    if (adminOnly && user.access_level !== "admin") return <Navigate to="/" replace />;
    return children;
}

function AppShell({ children }) {
    return (
        <div style={{ paddingTop: 62, minHeight: "100vh" }}>
            <TopBar />
            {children}
        </div>
    );
}

function AppRoutes() {
    const { user } = useAuth();
    return (
        <Routes>
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />

            <Route
                path="/change-password"
                element={
                    <Protected allowWhenMustChange>
                        <AppShell>
                            <ChangePasswordPage />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/"
                element={
                    <Protected>
                        <AppShell>
                            <ErrorBoundary label="library"><LibraryPage /></ErrorBoundary>
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/templates/new"
                element={
                    <Protected>
                        <AppShell>
                            <ErrorBoundary label="template-editor"><TemplateEditorPage /></ErrorBoundary>
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/templates/:id/edit"
                element={
                    <Protected>
                        <AppShell>
                            <ErrorBoundary label="template-editor"><TemplateEditorPage /></ErrorBoundary>
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/templates/:id"
                element={
                    <Protected>
                        <AppShell>
                            <ErrorBoundary label="template-detail"><TemplateDetailPage /></ErrorBoundary>
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/settings"
                element={
                    <Protected adminOnly>
                        <AppShell>
                            <SettingsPage />
                        </AppShell>
                    </Protected>
                }
            />
            <Route
                path="/activity"
                element={
                    <Protected adminOnly>
                        <AppShell>
                            <ActivityPage />
                        </AppShell>
                    </Protected>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <DataProvider>
                    <AppRoutes />
                    <Toaster
                        position="bottom-center"
                        toastOptions={{
                            style: {
                                background: "#1A3E6D",
                                color: "#fff",
                                border: "1px solid #24528A",
                                borderRadius: 999,
                                padding: "10px 18px",
                                fontFamily: "Poppins, sans-serif",
                                fontWeight: 500,
                                fontSize: 13,
                            },
                        }}
                    />
                </DataProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}
